const _ = require('underscore');

// Generic helper functions
const is_junction = function (dictree) {
    return _.isObject(dictree) && !_.isArray(dictree) && !_.isEmpty(dictree);
};
const unpack_key = function (key) {
    if (_.isArray(key)) return key;
    return [key];
};
const extend_key = function (new_elem, string_key) {
    return unpack_key(new_elem).concat(unpack_key(string_key));
};

const is_empty_object = function (obj) {
    // we need that and not _.isEmpty because we're looking specifically for objects
    return obj
        && Object.keys(obj).length === 0
        && Object.getPrototypeOf(obj) === Object.prototype;
};

const decompose_in_paths = function (dictree) {
    // Transform an dictree into an array of "paths". A path is an
    // array of keys that corresponds to the different branches taken.

    const to_return = [];

    _.pairs(dictree).forEach(function (pair) {
        const field = pair[0];
        const value = pair[1];
        if (is_junction(value)) {
            const flat_dictree = decompose_in_paths(value);

            _.each(flat_dictree, function (elem) {
                to_return.push({
                    path: extend_key(field, elem.path),
                    value: elem.value,
                });
            });
        } else {
            to_return.push({ path: [field], value: value });
        }
    });
    return to_return;
};

const compose_dictree = function (source, target, override_prune) {
    // Applies a dictree as path to a target dictree. Replaces the target
    // elements by the source ones.

    target = _.clone(target) || {};

    _.forEach(source, function (source_path) {

        const splitted_key = source_path.path;
        const k0 = splitted_key[0];

        if (splitted_key.length === 1) {
            // We have reached a leaf.
            // Making sure we don't overide with empty object
            if (!target[k0] || !is_empty_object(source_path.value)) {
                target[k0] = source_path.value;
            }
            return target;
        }

        // replacing the node by a junction if it is a leaf
        target[k0] = is_junction(target[k0]) ? _.clone(target[k0]) : {};

        // if the source is setting a value here and we allow _to_prune to be adjusted (override_prune),
        // we just need to remove it from the target.
        if (target[k0]._to_prune && override_prune) {
            delete target[k0]._to_prune;
        }

        const new_source = [];
        new_source.push({ path: splitted_key.slice(1), value: source_path.value });
        target[k0] = compose_dictree(new_source, target[k0], override_prune);
    });
    return target;
};

const merge_dictrees = function (source, target) {
    return compose_dictree(decompose_in_paths(source), target);
};

const isImplicitPathToken = function (dictree_path) {
    const path = dictree_path.path;
    return path[path.length - 1] === '_to_array' && dictree_path.value;
};
const convert_implicit_arrays_from_paths = function (as_paths) {

    // We get the path we are interested in (signaled by a _to_array field)
    const implicit_array_paths = _.chain(as_paths)
        .filter(isImplicitPathToken)
        .map(function (dictree_path) {
            return dictree_path.path.slice(0, -1);
        })
        .value();


    const paths_in_array = {};
    const normal_paths = [];
    // We separate the path that are part of an array from the other ones (the
    // normal ones).
    _.each(as_paths, function (path) {
        const is_implicit_token = isImplicitPathToken(path);

        const in_arrays = [];

        _.each(implicit_array_paths, function (in_array_path) {
            // We take all the sub path of the array path
            if (_.isEqual(in_array_path, path.path.slice(0, in_array_path.length))) {

                // We ignore the token for the outer level arrays (we need to
                // keep them for the nested arrays
                if (is_implicit_token && in_array_path.length === path.path.length - 1) return;
                in_arrays.push(in_array_path);
            }
        });
        if (is_implicit_token && _.isEmpty(in_arrays)) return;
        if (_.isEmpty(in_arrays)) return normal_paths.push(path);


        // We keep only the paths of the outer array if the item is in a nested
        // array.
        const array_path = _.min(in_arrays, function (array) { return array.length; });
        const array_id = JSON.stringify(array_path);

        if (!paths_in_array[array_id]) paths_in_array[array_id] = [];
        paths_in_array[array_id].push(path);
    });

    // Creating a bit paths list with the arrays created
    const all_paths = normal_paths.concat(_.map(paths_in_array, function (items, array_path) {
        array_path = JSON.parse(array_path);

        const array_value = _.chain(items)
            // We group the paths belonging to the same array items
            .groupBy(function (element) { return element.path[array_path.length]; })

            // We recreate the dictree for the paths inside the array (i.e. we
            // build the item).
            .map(function (paths, item_id) {

                const relative_paths = _.map(paths, function (item_path) {
                    return {
                        path: item_path.path.slice(array_path.length),
                        value: item_path.value,
                    };
                });

                const inner_dictree = convert_implicit_arrays_from_paths(relative_paths);
                const item_value = compose_dictree(inner_dictree)[item_id];
                item_value._id = item_id;

                return item_value;
            })
            .sortBy('_id')
            .sortBy('order')
            .map(function (item) {
                delete item.order;
                return item;
            })
            .value();

        return { path: array_path, value: array_value };
    }));

    return all_paths;
};

// Public API
// Base functions that work with dict paths

/**
 * Return a decompose structure of a dictree. Array are not decomposed.
 *
 * @param {Object} dictree
 * @return {Array.<{path: String, value}>}
 */
exports.decompose = decompose_in_paths;

/**
 * Apply list of decomposed path to an existing object
 *
 * @param {Array.<{path: String, value}>} source
 * @param {Object} target
 * @return {Object}
 */
exports.compose_into = compose_dictree;

/**
 * Creates flat dictionary with json arrays as keys
 *
 * @param {Object} dictree
 * @return {Object}
 */
exports.flatten = function (dictree) {
    //Transforms the dictree into a one level dictree with composed keys (json
    //encoded array of the path to the leaf
    return _.chain(decompose_in_paths(dictree))
        .map(function (paths) { return [JSON.stringify(paths.path), paths.value]; })
        .object()
        .value();
};

/**
 * Takes a flattened dictree and overrides the data in the target.
 *
 * @param {Object} flattened,
 * @param {Object} target
 * @return {Object}
 */
exports.unflatten_into = function (flattened, target) {
    //
    const paths = _.chain(flattened)
        .pairs()
        .map(function (paths) {
            return {
                path: JSON.parse(paths[0]),
                value: paths[1]
            };
        })
        .value();

    return compose_dictree(paths, target);
};

/**
 * Find difference between two tree. The result is whats missing in
 * current from previous
 *
 * @param {Object} current dictree
 * @param {Object} previous dictree to compare with
 * @return {Object}
 */
exports.diff = function (tree, ...others) {
    // backward: it supports multiple tree, but not used anywhere
    let merged;
    if (others.length > 1) {
        merged = exports.merge(others);
    } else {
        merged = (others || [])[0];
        if (!merged) {
            throw new Error('Missing tree to diff with');
        }
    }
    const flattened = exports.flatten(exports.prune(tree));
    const othersflattened = exports.flatten(exports.prune(merged));
    const flattenedkeys = [];  // built during filter below
    // round 1: filter path/value pairs down to just differences
    const diffpairs = _.chain(flattened).pairs()
        .filter(function (pair) {
            const otherval = othersflattened[pair[0]];
            flattenedkeys.push(pair[0]);
            if (_.isUndefined(otherval)) {
                // consider this key only if this value already wasn't undefined
                return !_.isUndefined(pair[1]);
            }
            if (_.isArray(otherval)) {
                return !_.isEqual(otherval, pair[1]);
            }
            // objects only appear as leaves when they're empty
            // so if otherval is also an object we know we're comparing two empty object leaves
            // https://github.com/Spotme/lib-leatherman/blob/7051790d4510bf90eaa27994edb08301112d6cd7/test/dictree.js#L34-40
            if (_.isObject(otherval) && _.isObject(pair[1])) {
                return false;
            }
            // compare primitive or one-sided object leaf
            // there's no need to do a deep equals comparison here
            return otherval !== pair[1];
        })
        .map(function (paths) {
            return {
                path: JSON.parse(paths[0]),
                value: paths[1]
            };
        })
        .value();
    // round 2: find deleted key/value pairs and prune them in the composed dictree
    // ⚑ TODO: Make deleted pruning step optional?
    _.chain(othersflattened).keys()
        .forEach(function (path) {
            let currPath, currPathStr, currPathStrSub, lastPathStr;
            function deletedJunctionKeyPredicate(key) {
                return key.indexOf(currPathStrSub) === 0
                    && !_.isUndefined(flattened[key]);
            }
            if (!_.isUndefined(flattened[path])) {
                return;
            }
            // reduce path down until we hit the lowest level defined junction for prune
            currPath = JSON.parse(path);
            while (currPath.length) {
                currPathStr = JSON.stringify(currPath);
                currPathStrSub = currPathStr.substr(0, currPathStr.length - 1);
                // does our current path match any of the keys in the original tree and relate to a non-undefined value?
                if (flattenedkeys.some(deletedJunctionKeyPredicate)) {
                    break;
                }
                currPath.pop();
                lastPathStr = currPathStr;
            }
            // ignore would be pruning case when prior junction is now a primitive
            if (!_.isUndefined(flattened[currPathStr])) {
                // uh oh ! the junction is actually a primitive now! ignore!
                return;
            }
            if (lastPathStr) {
                const lastPathSegments = JSON.parse(lastPathStr);
                diffpairs.push({
                    path: lastPathSegments,
                    value: { _to_prune: true }
                });
            }
        });
    return compose_dictree(diffpairs, {});
};

/**
 * Merges the array of dictree into one unique dictree. Items with higher index
 * are applied on top of other arrays. Note that non other types of nodes (numbers,
 * strings, array) completelyoverride any array at the same position.
 * Later dictree override properties of dictrees which come before in the list.
 *
 * @param {Array.<Object>} trees dictrees to merge
 * @return {Object}
 */
exports.merge = function (trees) {
    // Merges the list of dictrees.
    //
    let merged = {};
    _.each(trees, function (tree) {
        merged = merge_dictrees(tree, merged);
    });

    return merged;
};

/**
 * Return a copy of the object with implicit arrays turned into an array
 *
 * @param {Object}
 * @return {Object}
 */
exports.convert_implicit_arrays = function (dictree) {
    const as_paths = decompose_in_paths(dictree);
    const arrays_converted = convert_implicit_arrays_from_paths(as_paths);
    return compose_dictree(arrays_converted);
};

/**
 * Return a "copy" of the object with implicit pruned leaf removed
 *
 * @param {Object} dictree
 * @return {Object}
 */
exports.prune = function (dictree) {
    if (_.isArray(dictree)) {
        const pruned = [];
        for (const item of dictree) {
            if (!item) {
                pruned.push(item);
            } else {
                const prunedItem = exports.prune(item);
                if (prunedItem) {
                    pruned.push(prunedItem);
                }
            }
        }
        return pruned;
    }
    if (_.isObject(dictree)) {
        if (dictree._to_prune === true) {
            return undefined;
        }
        const pruned = {};
        for (const [key, item] of Object.entries(dictree)) {
            if (!item) {
                pruned[key] = item;
            } else {
                const prunedItem = exports.prune(item);
                if (prunedItem) {
                    pruned[key] = prunedItem;
                }
            }
        }
        return pruned;
    }
    return dictree;
};

/**
 * Create a patch from the diff of two trees and apply an existing patch at the
 * top of the merge resulting in the source dictree.
 *
 * @param {Object} current the current dictree
 * @param {Object} previous the dictree to compare with
 * @param {Object} [existing] an existing patch
 * @return {Object}
 */
exports.build_patch = function (current, previous, existing) {
    const diff = exports.diff(current, previous);
    if (!existing) {
        return diff;
    }
    const changes = decompose_in_paths(diff);
    return compose_dictree(changes, existing, true);
};
